import { QUERY_KEYS } from "@/libraries/constant";
import type {
	AddRemoveMemberCasePayloadSchema,
	CreateCaseInfoPayloadSchema,
	CreateCasePayloadSchema,
	ExportCasePayloadSchema,
	MedicalNoteFormSchema,
	PatientVisitFormSchema,
	PostAddLocationPayloadSchema,
	UpdateCasePayloadSchema,
} from "@/schemas";
import type { caseService } from "@/services";
import type {
	AssignPatientIntoCasePayloadSchema,
	AssignUnassignTeamPayload,
	Case,
	CreateCaseResponse,
	GetCasePatientVisitResponse,
	GetCaseResponse,
	PostAddPatientLocationResponse,
	PostRequestAccessCasePayload,
	ResError,
	UpdateCaseResponse,
} from "@/types";
import { useMutation } from "@tanstack/react-query";

function useCaseMutation() {
	const update = useMutation<
		UpdateCaseResponse,
		ResError,
		UpdateCasePayloadSchema
	>({
		mutationKey: [QUERY_KEYS.MUTATE_CASE_UPDATE],
	});

	const create = useMutation<
		CreateCaseResponse,
		ResError,
		CreateCasePayloadSchema
	>({
		mutationKey: [QUERY_KEYS.MUTATE_CASE_CREATE],
	});

	const assignPatient = useMutation<
		GetCaseResponse,
		ResError,
		AssignPatientIntoCasePayloadSchema
	>({
		mutationKey: [QUERY_KEYS.MUTATE_CASE_ASSIGN_PATIENT],
	});

	const addLocation = useMutation<
		PostAddPatientLocationResponse,
		ResError,
		PostAddLocationPayloadSchema
	>({
		mutationKey: [QUERY_KEYS.MUTATE_CASE_ADD_PATIENT_LOCATION],
	});

	const addPatientVisit = useMutation<
		GetCasePatientVisitResponse,
		ResError,
		PatientVisitFormSchema
	>({
		mutationKey: [QUERY_KEYS.MUTATE_PATIENT_VISIT_CREATE],
	});

	const addCaseInfo = useMutation<
		unknown,
		ResError,
		CreateCaseInfoPayloadSchema
	>({
		mutationKey: [QUERY_KEYS.MUTATE_CASE_INFO_CREATE],
	});

	const closedCase = useMutation<unknown, ResError, Case["id"]>({
		mutationKey: [QUERY_KEYS.MUTATE_CASE_CLOSED],
	});

	const reopenCase = useMutation<unknown, ResError, Case["id"]>({
		mutationKey: [QUERY_KEYS.MUTATE_CASE_REOPEN],
	});

	const leaveCase = useMutation<unknown, ResError, Case["id"]>({
		mutationKey: [QUERY_KEYS.MUTATE_CASE_LEAVE],
	});

	const addMembers = useMutation<
		unknown,
		ResError,
		AddRemoveMemberCasePayloadSchema
	>({
		mutationKey: [QUERY_KEYS.MUTATE_CASE_ADD_MEMBER],
	});

	const removeMembers = useMutation<
		unknown,
		ResError,
		AddRemoveMemberCasePayloadSchema
	>({
		mutationKey: [QUERY_KEYS.MUTATE_CASE_REMOVE_MEMBER],
	});

	const assignTeam = useMutation<unknown, ResError, AssignUnassignTeamPayload>({
		mutationKey: [QUERY_KEYS.MUTATE_CASE_ASSIGN_TEAM],
	});

	const unassignTeam = useMutation<
		unknown,
		ResError,
		AssignUnassignTeamPayload
	>({
		mutationKey: [QUERY_KEYS.MUTATE_CASE_UNASSIGN_TEAM],
	});

	const requestAccess = useMutation<
		unknown,
		ResError,
		PostRequestAccessCasePayload
	>({
		mutationKey: [QUERY_KEYS.MUTATE_CASE_REQUEST_ACCESS],
	});

	const sendEmail = useMutation<unknown, Error, ExportCasePayloadSchema>({
		mutationKey: [QUERY_KEYS.MUTATE_CASE_SEND_EMAIL],
	});

	const createMedicalNote = useMutation<
		unknown,
		ResError,
		MedicalNoteFormSchema
	>({
		mutationKey: [QUERY_KEYS.MUTATE_CREATE_CASE_MEDICAL_NOTES],
	});

	const updateMedicalNote = useMutation<
		unknown,
		ResError,
		MedicalNoteFormSchema
	>({
		mutationKey: [QUERY_KEYS.MUTATE_UPDATE_CASE_MEDICAL_NOTES],
	});

	const deleteMedicalNote = useMutation<unknown, ResError, number>({
		mutationKey: [QUERY_KEYS.MUTATE_DELETE_CASE_MEDICAL_NOTES],
	});

	const signMedicalNote = useMutation<
		unknown,
		ResError,
		{ caseId: number | string; id: number | string }
	>({
		mutationKey: [QUERY_KEYS.MUTATE_SIGN_CASE_MEDICAL_NOTE],
	});

	const createLetter = useMutation<
		Awaited<ReturnType<typeof caseService.createLetter>>,
		ResError,
		Parameters<typeof caseService.createLetter>[0]
	>({
		mutationKey: [QUERY_KEYS.MUTATE_CASE_LETTERS_CREATE_NOTES],
	});

	const updateLetter = useMutation<
		Awaited<ReturnType<typeof caseService.updateLetter>>,
		ResError,
		Parameters<typeof caseService.updateLetter>[0]
	>({
		mutationKey: [QUERY_KEYS.MUTATE_CASE_LETTERS_UPDATE_NOTES],
	});

	const deleteLetter = useMutation<
		Awaited<ReturnType<typeof caseService.deleteLetter>>,
		ResError,
		Parameters<typeof caseService.deleteLetter>[0]
	>({
		mutationKey: [QUERY_KEYS.MUTATE_CASE_LETTERS_DELETE_NOTES],
	});

	const sendLetter = useMutation<
		Awaited<ReturnType<typeof caseService.sendLetter>>,
		ResError,
		Parameters<typeof caseService.sendLetter>[0]
	>({
		mutationKey: [QUERY_KEYS.MUTATE_CASE_LETTERS_EMAIL_NOTES],
	});

	return {
		create,
		update,
		assignPatient,
		addLocation,
		addPatientVisit,
		addCaseInfo,
		closedCase,
		reopenCase,
		leaveCase,
		addMembers,
		removeMembers,
		assignTeam,
		unassignTeam,
		requestAccess,
		sendEmail,
		createMedicalNote,
		updateMedicalNote,
		deleteMedicalNote,
		signMedicalNote,
		createLetter,
		updateLetter,
		deleteLetter,
		sendLetter,
	};
}

export default useCaseMutation;
